/**
 * Locale Context
 *
 * @author    Frontend Design Systems <FrontendDesignSystems@wayfair.com>
 * @copyright 2019 Wayfair LLC - All rights reserved
 */

import React, {createContext} from 'react';
import PropTypes from 'prop-types';
import {LOCALES} from './locale-constants.js';

export const localeContext = createContext();

const localeValues = Object.values(LOCALES);

export function Provider({children, locale}) {
  if (typeof locale === 'undefined') {
    throw new Error(
      'No locale provided as props to the locale Provider. You must provide a locale to the Provider component.'
    );
  } else if (!localeValues.includes(locale)) {
    throw new Error(
      `The locale provided as a prop is not a supported locale. You provided: "${locale}".

The locale value should be one of the values from the LOCALES constant.`
    );
  }
  return (
    <localeContext.Provider value={locale}>{children}</localeContext.Provider>
  );
}

Provider.propTypes = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.oneOf(Object.values(LOCALES)).isRequired,
};

export function Consumer({children}) {
  return (
    <localeContext.Consumer>
      {(locale) => {
        if (typeof locale === 'undefined') {
          throw new Error(
            'No locale found in context. Ensure the root of the application is wrapped in a locale Provider with a valid `locale` prop.'
          );
        }
        return children(locale);
      }}
    </localeContext.Consumer>
  );
}

Consumer.propTypes = {
  children: PropTypes.func.isRequired,
};

export {LOCALES};
